/**
 * SoftwareApplicationSchema — injects SoftwareApplication + Product JSON-LD
 * into <head> so Google (and AI Overviews) see Central Rentals as a
 * structured product with pricing, features, and OS support.
 *
 * Import into any page (Landing, Pricing, ROI Calculator) — mount as a
 * component; it uses a portal to inject the JSON-LD script into <head>.
 */
import { useEffect } from "react";

const SCHEMA_ID = "crc-software-app-jsonld";

// Feature categories aligned to real Central Rentals modules.
const FEATURE_LIST = [
  "Smart tenant screening (Certn integration)",
  "Automated rent roll reconciliation",
  "Pre-Authorized Debit (PAD) rent collection",
  "Interac e-Transfer auto-reconciliation",
  "Provincial legal notice generator (Ontario N4/L1/N12, BC Form K, Alberta RTA)",
  "T776-ready Canadian tax export",
  "Maintenance ticketing with photo uploads",
  "Late-rent autopilot with LTB-admissible audit trail",
  "Rent-on-time score for tenants",
  "PIPEDA-compliant Canadian data residency",
];

const SOFTWARE_APP = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Central Rentals Canada",
  alternateName: ["Central Rentals", "Central Rentals Canada software"],
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Property Management Software",
  operatingSystem: "Web, iOS, Android",
  url: "https://centralrentals.ca",
  description:
    "Canadian property management software built for landlords and rental managers across all 13 provinces and territories. Central Rentals Canada delivers Certn-powered tenant screening, PAD + Interac rent collection, provincial legal notice generation (N4, L1, Form K, RTA), maintenance ticketing, and T776-ready tax exports — all PIPEDA-compliant with Canadian data residency.",
  featureList: FEATURE_LIST,
  offers: [
    {
      "@type": "Offer",
      name: "Starter",
      price: "60.00",
      priceCurrency: "CAD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "60.00",
        priceCurrency: "CAD",
        unitCode: "MON",
        unitText: "per month",
      },
      description: "Solo landlords, 1–10 units. Flat CA$35/month.",
      availability: "https://schema.org/InStock",
      url: "https://centralrentals.ca/pricing",
    },
    {
      "@type": "Offer",
      name: "Growth / Professional",
      price: "150.00",
      priceCurrency: "CAD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "150.00",
        priceCurrency: "CAD",
        unitCode: "MON",
        unitText: "per month",
      },
      description: "Growing portfolios, 11–50 units. Flat CA$50/month.",
      availability: "https://schema.org/InStock",
      url: "https://centralrentals.ca/pricing",
    },
    {
      "@type": "Offer",
      name: "Enterprise",
      price: "250.00",
      priceCurrency: "CAD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "250.00",
        priceCurrency: "CAD",
        unitCode: "MON",
        unitText: "per month",
      },
      description: "Multi-property managers, 50+ units. Flat CA$80/month.",
      availability: "https://schema.org/InStock",
      url: "https://centralrentals.ca/pricing",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "42",
    bestRating: "5",
    worstRating: "1",
  },
  publisher: {
    "@type": "Organization",
    "name": "Central Rentals Canada",
    url: "https://centralrentals.ca",
    areaServed: { "@type": "Country", name: "Canada" },
  },
  softwareRequirements: "Modern web browser (Chrome, Safari, Firefox, Edge) or PWA-capable mobile device.",
  inLanguage: ["en-CA", "fr-CA"],
  audience: {
    "@type": "Audience",
    audienceType: "Canadian landlords, property managers, and tenants",
  },
};

// Product schema for the "Central Rentals platform" itself — used
// by Google Shopping / AI product cards. Duplicates some info with
// SoftwareApplication but Product is what Google Merchant expects.
const PRODUCT = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Central Rentals Canada — Property Management Platform",
  description:
    "Canadian property management software with smart tenant screening, automated rent roll reconciliation, provincial compliance tools, and CRA-ready T776 tax exports.",
  brand: { "@type": "Brand", name: "Central Rentals Canada" },
  category: "Business & Productivity Software > Property Management Software",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "60",
    highPrice: "250",
    priceCurrency: "CAD",
    offerCount: "3",
    availability: "https://schema.org/InStock",
    url: "https://centralrentals.ca/pricing",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "42",
  },
};

// Organization schema — anchors the brand name so Google surfaces the
// knowledge panel with logo + contact + social profiles. Includes the
// real Alberta Corporate Registry identifiers so Central Rentals Canada
// Inc. can be verified as a government-registered business.
const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://centralrentals.ca/#organization",
  name: "Central Rentals Canada",
  legalName: "Central Rentals Canada Inc.",
  url: "https://centralrentals.ca",
  logo: "https://centralrentals.ca/logo.png",
  description:
    "Canadian property management software company serving landlords, tenants, and property managers across all 13 provinces and territories.",
  areaServed: { "@type": "Country", name: "Canada" },
  address: {
    "@type": "PostalAddress",
    addressCountry: "CA",
  },
  isicV4: "6820",  // Renting and operating of own or leased real estate
  naics: "531311", // Residential Property Managers (US) / equivalent NAICS Canada
  knowsAbout: [
    "Canadian property management",
    "Ontario LTB N4 notices",
    "Quebec TAL leases and Loi 41",
    "BC RTB compliance",
    "Alberta RTDRS",
    "Tenant screening in Canada",
    "CRA T776 rental income tax",
    "PIPEDA-compliant rental data",
  ],
  award: [],
  sameAs: [
    "https://centralrentals.ca",
    "https://www.linkedin.com/company/central-rentals-canada",
    // Google Business Profile citations across Central Rentals' service
    // area — each one is a live GBP that Google can reconcile back to
    // this Organization for Local Pack rankings.
    "https://maps.app.goo.gl/3R94HfmbLnbNgUvk9?g_st=ic",
    "https://maps.app.goo.gl/xNTfCYwvYybMADkT8",
    "https://maps.app.goo.gl/dvb9eQTLSW6VBhab7",
    "https://maps.app.goo.gl/Gv1trS78h4yjK3818",
    "https://maps.app.goo.gl/3WVMMqRoFMEmLcSr6",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "contact@centralrentals.ca",
      telephone: "+1-780-405-2498",
      availableLanguage: ["en", "fr"],
      areaServed: "CA",
      hoursAvailable: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday", "Tuesday", "Wednesday", "Thursday",
            "Friday", "Saturday", "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
      ],
    },
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "contact@centralrentals.ca",
      telephone: "+1-780-405-2498",
      availableLanguage: ["en", "fr"],
      areaServed: "CA",
      hoursAvailable: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday", "Tuesday", "Wednesday", "Thursday",
            "Friday", "Saturday", "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
      ],
    },
  ],
  // Central Rentals runs as a 24/7 online SaaS — surface that explicitly
  // so Google's Knowledge Panel stops rendering third-party "Sat/Sun
  // Closed" hints from stale BBB / GBP listings.
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday", "Tuesday", "Wednesday", "Thursday",
        "Friday", "Saturday", "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
  ],
};

// WebSite + SearchAction — enables the Google sitelinks search box
// when someone searches "Central Rentals Canada".
const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://centralrentals.ca/#website",
  name: "Central Rentals Canada",
  alternateName: "Canadian property management software",
  url: "https://centralrentals.ca",
  inLanguage: ["en-CA", "fr-CA"],
  publisher: { "@id": "https://centralrentals.ca/#organization" },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://centralrentals.ca/search?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
};

// FAQPage — answers the most common intent queries directly, boosting
// zero-click AI Overview and Featured Snippet coverage.
const FAQ_PAGE = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is the best Canadian property management software in 2026?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Central Rentals Canada is a Toronto-built property management platform designed for Canadian landlords. It ships province-specific lease templates (N4/L1 in Ontario, Form K in BC, TAL in Quebec), CAD-native Stripe rent collection with Interac + PAD, Certn-powered tenant screening, and CRA T776 exports — starting at CA$35/month for 1–10 units.",
      },
    },
    {
      "@type": "Question",
      name: "How much does Canadian property management software cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Central Rentals Canada uses flat monthly pricing: CA$35/month for 1–10 units (Starter), CA$50/month for 11–50 units (Growth), and CA$80/month for 50+ units (Enterprise). Annual prepay saves 17%. A Paid plans from CA$35/month is available with cancel any time.",
      },
    },
    {
      "@type": "Question",
      name: "Does Central Rentals Canada support all Canadian provinces?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes — Central Rentals Canada supports all 13 provinces and territories. Province-specific notice templates are included (Ontario N1/N4/N5/L1/L2, BC Form K + RTB, Quebec TAL + Loi 41, Alberta RTA + RTDRS, and equivalents for MB/SK/NS/NB/PE/NL/NT/YT/NU).",
      },
    },
    {
      "@type": "Question",
      name: "Is Central Rentals Canada PIPEDA compliant?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Central Rentals Canada is fully PIPEDA-compliant with Canadian data residency. Tenant screening runs through Certn (Canada's leading bureau) using Equifax Canada, CPIC criminal checks, and eviction-history data — never sent outside Canadian borders.",
      },
    },
  ],
};

export default function SoftwareAppSchema() {
  useEffect(() => {
    // Idempotent: remove any prior injection first.
    document.getElementById(SCHEMA_ID)?.remove();
    const script = document.createElement("script");
    script.id = SCHEMA_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify([SOFTWARE_APP, PRODUCT, ORGANIZATION, WEBSITE, FAQ_PAGE]);
    document.head.appendChild(script);
    return () => { document.getElementById(SCHEMA_ID)?.remove(); };
  }, []);
  return null;
}
